import { createContext, useContext } from 'react';

/**
 * Raw showroom action config, parsed from the entry URL's query params. Captured once by
 * `ShowroomPage` and supplied via context — reading `useLocation()` here would fail because the
 * showroom redirects to a query-less URL (`buildConfiguratorUrl`) right after the initial load.
 */
export interface ShowroomActionParams {
    closeLabel?: string;
    completeLabel?: string;
    closeUrl?: string;
    completeUrl?: string;
}

const ShowroomActionsContext = createContext<ShowroomActionParams>({});

/** Supplies the showroom close/complete config. Absent outside the showroom → buttons stay hidden. */
export const ShowroomActionsProvider = ShowroomActionsContext.Provider;

export interface ShowroomActions {
    /** Label for the close button, from the `close_label` query param. Undefined hides the button. */
    closeLabel?: string;
    /** Label for the complete button, from the `complete_label` query param. Undefined hides the button. */
    completeLabel?: string;
    onClose(): void;
    onComplete(): void;
}

// Navigate the top-level window (breaks out of the showroom iframe). Assigning `location.href` on the
// top frame is permitted cross-origin; only reading it is blocked. Falls back to the current window
// when not embedded.
const navigateTop = (url: string) => {
    const target = window.top ?? window;
    target.location.href = url;
};

// Notify the embedding host page. Matches the existing `showroom-resize` postMessage pattern;
// only fires when actually embedded in an iframe.
const emit = (type: string) => {
    if (window.self !== window.top) {
        window.parent?.postMessage({ type }, '*');
    }
};

/**
 * Host-configurable close / complete actions for the anonymous showroom topbar. The button labels /
 * target URLs come from the entry URL's query params (see `ShowroomActionParams`):
 *   close_label / complete_label — button labels (presence toggles the button; shown verbatim, not i18n)
 *   close_url / complete_url     — when set, navigate the top window here instead of emitting an event
 */
export const useShowroomActions = (): ShowroomActions => {
    const { closeLabel, completeLabel, closeUrl, completeUrl } = useContext(ShowroomActionsContext);

    return {
        closeLabel,
        completeLabel,
        onClose: () => (closeUrl ? navigateTop(closeUrl) : emit('showroom-close')),
        onComplete: () => (completeUrl ? navigateTop(completeUrl) : emit('showroom-complete')),
    };
};
